import { Socket } from 'socket.io-client';
import { SocketChatSession } from '../types/chat';
import { SocketMessage, SocketType } from '../types/socket';

interface TypingHelperGuardOptions {
  sessionFilter?: string | null;
  currentUserId?: string | null;
}

export class TypingHelper {
  static createTypingThrottler = (cooldownMs: number = 1000) => {
    let lastEmit = 0;

    return (callback: () => void) => {
      const now = Date.now();
      if (now - lastEmit >= cooldownMs) {
        lastEmit = now;
        callback();
      }
    };
  };

  static createTypingTimeout = (onTimeout: () => void, timeoutMs: number = 2000) => {
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    const trigger = (onActive?: () => void) => {
      if (onActive) {
        onActive();
      }

      if (timeoutId) {
        clearTimeout(timeoutId);
      }

      timeoutId = setTimeout(() => {
        timeoutId = null;
        onTimeout();
      }, timeoutMs);
    };

    const dispose = () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
    };

    return { trigger, dispose };
  };

  static createSessionTypingTracker = (timeoutMs: number = 2000) => {
    const timeouts: Record<string, ReturnType<typeof setTimeout>> = {};

    const trigger = (
      sessionId: string,
      onActive: () => void,
      onInactive: () => void
    ) => {
      onActive();

      if (timeouts[sessionId]) {
        clearTimeout(timeouts[sessionId]);
      }

      timeouts[sessionId] = setTimeout(() => {
        delete timeouts[sessionId];
        onInactive();
      }, timeoutMs);
    };

    const clear = (sessionId: string) => {
      if (timeouts[sessionId]) {
        clearTimeout(timeouts[sessionId]);
        delete timeouts[sessionId];
      }
    };

    const dispose = () => {
      Object.values(timeouts).forEach(timeoutId => clearTimeout(timeoutId));
      Object.keys(timeouts).forEach(key => delete timeouts[key]);
    };

    return { trigger, clear, dispose };
  };
  static emitTypingMessage = (
    socketInstance: Socket | null | undefined,
    session: SocketChatSession | null | undefined,
    senderId?: string | null,
    extra?: Partial<SocketMessage>
  ) => {
    if (!socketInstance?.connected || !session?.id) {
      return;
    }

    const typingMessage: SocketMessage = {
      session,
      socket: SocketType.TYPING,
      senderId: senderId ?? null,
      timestamp: new Date(),
      ...extra
    };

    socketInstance.emit('message', typingMessage);
  };

  static extractTypingSessionId = (
    message: SocketMessage,
    options: TypingHelperGuardOptions = {}
  ): string | null => {
    const sessionId = message.session?.id || null;
    if (!sessionId) {
      return null;
    }

    const { sessionFilter, currentUserId } = options;
    if (sessionFilter && sessionId !== sessionFilter) {
      return null;
    }

    if (currentUserId && message.senderId === currentUserId) {
      return null;
    }

    return sessionId;
  };
}


